import React from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

// Styled components
const FormationContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  position: "relative",
  minHeight: 500,
  backgroundColor: "#ffffff",
  overflow: "hidden",
}));

const SectionHeader = styled(Typography)(({ theme }) => ({
  backgroundColor: "#000000",
  color: "#ffffff",
  padding: theme.spacing(1),
  textAlign: "right",
  fontWeight: "bold",
}));

const PositionCard = styled(Paper)(({ theme }) => ({
  border: "1px solid #000000",
  height: "100%",
  minHeight: 150,
  maxHeight: 400,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  transition: theme.transitions.create(["height"], {
    duration: theme.transitions.duration.short,
  }),
}));

const PositionHeader = styled(Box)(({ theme }) => ({
  borderBottom: "1px solid #cccccc",
  padding: theme.spacing(0.5),
  textAlign: "center",
  backgroundColor: "#f5f5f5",
}));

const PlayerItem = styled(Box)(({ theme, depth }) => ({
  padding: theme.spacing(0.5),
  borderTop: "1px dashed #cccccc",
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  backgroundColor:
    depth === 1 ? "#e8f5e8" : depth === 2 ? "#fff3e0" : "#f5f5f5",
  "&:first-of-type": {
    borderTop: "none",
  },
  "&:hover": {
    backgroundColor:
      depth === 1 ? "#d4edda" : depth === 2 ? "#fed7aa" : "#e9ecef",
    "& .player-actions": {
      opacity: 1,
    },
  },
}));

const PlayerContent = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
}));

const PlayerActions = styled(Box)(({ theme }) => ({
  opacity: 0,
  transition: theme.transitions.create(["opacity"], {
    duration: theme.transitions.duration.short,
  }),
  display: "flex",
  gap: theme.spacing(0.5),
}));

const AddPlayerButton = styled(Button)(({ theme }) => ({
  width: "100%",
  minHeight: 40,
  borderStyle: "dashed",
  borderColor: theme.palette.primary.main,
  color: theme.palette.primary.main,
  "&:hover": {
    backgroundColor: theme.palette.primary.light + "20",
  },
}));

// Simplified position layout definitions
const DEPTH_CHART_LAYOUT = {
  offense: {
    title: "OFFENSE",
    rows: [
      // First row - WR, LT, LG, C, RG, RT, TE
      [
        { id: "wr1", label: "WR1" },
        { id: "lt", label: "LT" },
        { id: "lg", label: "LG" },
        { id: "c", label: "C" },
        { id: "rg", label: "RG" },
        { id: "rt", label: "RT" },
        { id: "te", label: "TE" },
      ],
      // Second row - WR, QB, RB
      [
        { id: "wr2", label: "WR2" },
        { id: "empty", label: "" },
        { id: "empty", label: "" },
        { id: "qb", label: "QB" },
        { id: "rb", label: "RB" },
        { id: "empty", label: "" },
        { id: "empty", label: "" },
      ],
    ],
  },
  defense: {
    title: "DEFENSE",
    rows: [
      // First row - FS, SS
      [
        { id: "empty", label: "" },
        { id: "empty", label: "" },
        { id: "fs", label: "FS" },
        { id: "empty", label: "" },
        { id: "ss", label: "SS" },
        { id: "empty", label: "" },
        { id: "empty", label: "" },
      ],
      // Second row - CB, OLB, MLB, OLB, CB
      [
        { id: "cb", label: "CB" },
        { id: "empty", label: "" },
        { id: "olb", label: "OLB" },
        { id: "mlb", label: "MLB" },
        { id: "olb", label: "OLB" },
        { id: "empty", label: "" },
        { id: "cb", label: "CB" },
      ],
      // Third row - DE, DT, DT, DE
      [
        { id: "empty", label: "" },
        { id: "de", label: "DE" },
        { id: "dt", label: "DT" },
        { id: "empty", label: "" },
        { id: "dt", label: "DT" },
        { id: "de", label: "DE" },
        { id: "empty", label: "" },
      ],
    ],
  },
};

const FormationView = ({
  positions,
  players,
  onAddPlayer,
  onRemovePlayer,
  onMovePlayer,
  onToggleStarter,
  onViewDetails,
  onEditPlayer,
  onDeletePlayer,
  unit = "offense",
  maxPlayersPerPosition = 3,
}) => {
  // Render a player position card with its players
  const renderPositionCard = (positionId) => {
    if (positionId === "empty") {
      return <Box sx={{ height: "100%", minHeight: 120 }} />;
    }

    // Find players at this position
    const positionPlayers = players.filter(
      (player) => player.position === positionId
    );

    // Sort by depth
    positionPlayers.sort((a, b) => (a.depth || 99) - (b.depth || 99));

    const canAddMore = positionPlayers.length < maxPlayersPerPosition;

    // Helper function to get depth label
    const getDepthLabel = (depth) => {
      switch (depth) {
        case 1:
          return "1st";
        case 2:
          return "2nd";
        case 3:
          return "3rd";
        default:
          return `${depth}th`;
      }
    };

    return (
      <PositionCard elevation={1}>
        <PositionHeader>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="subtitle2" fontWeight="bold">
              {positionId.toUpperCase()}
            </Typography>
            <Chip
              label={positionPlayers.length}
              size="small"
              color={positionPlayers.length === 0 ? "error" : "primary"}
              sx={{ height: 20, fontSize: "0.6rem" }}
            />
          </Box>
        </PositionHeader>

        <Box sx={{ flex: 1, overflow: "auto" }}>
          {/* Existing Players */}
          {positionPlayers.map((player, idx) => (
            <PlayerItem key={player.id} depth={player.depth}>
              <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                <Tooltip title="Drag to reorder">
                  <DragIndicatorIcon
                    sx={{
                      fontSize: 12,
                      color: "text.secondary",
                      mr: 0.5,
                      cursor: "grab",
                    }}
                  />
                </Tooltip>

                <PlayerContent>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: player.depth === 1 ? "bold" : "normal",
                      display: "block",
                      lineHeight: 1.2,
                    }}
                  >
                    <Chip
                      label={getDepthLabel(player.depth)}
                      size="small"
                      color={
                        player.depth === 1
                          ? "success"
                          : player.depth === 2
                          ? "warning"
                          : "default"
                      }
                      sx={{ height: 16, fontSize: "0.6rem", mr: 0.5 }}
                    />
                    {player.name || `Player #${player.number}`}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.6rem", display: "block" }}
                  >
                    #{player.number} • {player.year || "FR"}
                  </Typography>
                </PlayerContent>
              </Box>

              <PlayerActions className="player-actions">
                <Tooltip title="Edit Player">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditPlayer && onEditPlayer(player);
                    }}
                    sx={{ padding: 0.25 }}
                  >
                    <EditIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Remove Player">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeletePlayer
                        ? onDeletePlayer(player.id)
                        : onRemovePlayer(player.id);
                    }}
                    color="error"
                    sx={{ padding: 0.25 }}
                  >
                    <DeleteIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                </Tooltip>
              </PlayerActions>
            </PlayerItem>
          ))}

          {/* Add Player Button */}
          {canAddMore && (
            <Box sx={{ p: 0.5 }}>
              <AddPlayerButton
                size="small"
                variant="outlined"
                onClick={() =>
                  onAddPlayer(positionId, positionPlayers.length + 1)
                }
                startIcon={<AddIcon />}
              >
                Add Player
              </AddPlayerButton>
            </Box>
          )}

          {/* No players message */}
          {positionPlayers.length === 0 && (
            <Box sx={{ p: 2, textAlign: "center" }}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mb: 1 }}
              >
                No players assigned
              </Typography>
            </Box>
          )}
        </Box>

        {/* Position limit indicator */}
        {positionPlayers.length >= maxPlayersPerPosition && (
          <Box sx={{ p: 0.5, borderTop: "1px solid #e0e0e0" }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textAlign: "center", display: "block", fontSize: "0.6rem" }}
            >
              Position Full ({maxPlayersPerPosition} max)
            </Typography>
          </Box>
        )}
      </PositionCard>
    );
  };

  // Render the depth chart section
  const renderDepthChartSection = (section) => {
    return (
      <Box sx={{ mb: 3 }}>
        <SectionHeader variant="h6">{section.title}</SectionHeader>

        {section.rows.map((row, rowIndex) => (
          <Box
            key={`${section.title}-row-${rowIndex}`}
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: 1,
              mt: 1,
            }}
          >
            {row.map((position, posIndex) => (
              <Box key={`${position.id}-${posIndex}`}>
                {renderPositionCard(position.id)}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      <FormationContainer>
        <Box sx={{ p: 1 }}>
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={{
              mb: 2,
              textAlign: "center",
              bgcolor: "#f5f5f5",
              p: 1,
              border: "1px solid #ccc",
            }}
          >
            Depth Chart - {unit.toUpperCase()} (As of{" "}
            {new Date().toLocaleDateString()})
          </Typography>

          {unit === "offense" &&
            renderDepthChartSection(DEPTH_CHART_LAYOUT.offense)}
          {unit === "defense" &&
            renderDepthChartSection(DEPTH_CHART_LAYOUT.defense)}
        </Box>
      </FormationContainer>
    </Box>
  );
};

export default FormationView;
